import {Injectable} from '@angular/core';
import {TextureSizeService} from '../../3DView/Calculation/texture-size.service';
import {DistanceMeasureService} from '../../3DView/Calculation/distance-measure.service';
import {ContainerService} from '../../Utils/container.service';
import {Sex} from '../../3DView/Shape/modelType';
import {ModelDataService} from '../../Utils/model-data.service';
import {Color, Vector3} from 'three';
import {ColorCodingService} from '../../3DView/Painting/color-coding.service';
import {BrushService} from '../../3DView/Painting/brush.service';
import {PictureService} from '../../3DView/Picture/picture.service';
import {MouseControlsService} from '../../3DView/Control/mouse-controls.service';
import {ViewComponent} from '../../3DView/view.component';
import {ShapeService} from '../../3DView/Shape/shape.service';
import {LoaderService} from '../../3DView/Scene/loader.service';
import {Subject} from 'rxjs';
import {MarkingType} from '../../3DView/Painting/markingType';
import {Labels} from '../../3DView/Painting/labels';
import {PictureLandmarkService} from '../../3DView/Picture/PictureLandmark/picture-landmark.service';
import {StartUp} from '../../3DView/Scene/start-up';

import {GltfExporterService} from '../../Utils/gltf-exporter.service';
import { Patient } from 'projects/persistence/src/models/patient.model';
import {SensorService} from '../../3DView/Painting/sensor.service';
import {PolyPaintService} from '../../3DView/Painting/poly-paint.service';
import {TexturePaintService} from '../../3DView/Painting/texture-paint.service';
import {CameraService} from '../../3DView/Scene/camera.service';
import {OrbitControlsService} from '../../3DView/Control/orbit-controls.service';
import {RenderService} from '../../3DView/Scene/render.service';
import {PaintService} from '../../3DView/Painting/paint.service';

@Injectable({
    providedIn: 'root'
})
export class DataExchangeService {
    // private readonly _model: ModelType;
    private _burnSize = new Subject<any>();

    constructor(
        public patient: PatientService,
        public painting: PaintingService,
        public image: ImageService,
        private textureSizeService: TextureSizeService,
        private distanceMeasureService: DistanceMeasureService,
        private containerService: ContainerService,
        private mouseControlService: MouseControlsService,
        private shapeService: ShapeService,
        private loaderService: LoaderService,
        private exportGltfService: GltfExporterService,
        private startUpService: StartUp,
        private cameraService: CameraService,
        private orbitControlsService: OrbitControlsService,
        private renderService: RenderService) {
        // this.containerService.modelType = new ModelType();
        // this._model = new ModelType();
        this.loaderService.loaded.subscribe((loaded) => {
            if (loaded.valueOf() === 0) {
                this._burnSize.next(this.textureSizeService.calculateSurfaceProperties());
            }
        });

        // update zoom of camera after model is calculated needed for iphone
        this.loaderService.loaded.subscribe((loaded) => {
            if (loaded.valueOf() === 1) {
                this.updateViewSize();
                this.loaderService.loaded.observers.pop();
            }
        });
    }

    /**
     * returns the current loading manager status 0: start -> 1: complete
     * @returns number
     */
    public get loadingStatus(): number {
        return this.containerService.loadingStatus;
    }


    // results -----------------------------------------------------------------------------------------------------------------------------

    /**
     * returns Marked Area
     */
    public calculateBurnSize() {
        if (this.patient.modelChanged) {
            this.patient.changeModel();
        } else {
            this._burnSize.next(this.textureSizeService.calculateSurfaceProperties());
        }

    }

    get burnSize(): Subject<any> {
        return this._burnSize;
    }

    /**
     * returns distance between two points
     * @returns distance: number
     */
    get pointsDistance(): number {
        return this.distanceMeasureService.distance;
    }

    public exportGLTF(): void {
        this.exportGltfService.exportGLTF();
    }

    // view --------------------------------------------------------------------------------------------------------------------------------

    /**
     * Switches the view, which sets available operations from canvas.
     * @param editor the selector for the operations which are allowed.
     */
    public switchView(editor: string) {
        this.startUpService.switchView(editor);
    }

    /**
     * updates surface view to fit current view
     */
    public updateViewSize() {
        this.shapeService.surfaceView.resize(true);
        // this.mouseControlService.enableImageMode(true);  // todo is this needed mstifin
    }

    /**
     * sets camera to predefined position, and target point
     * @param camera: Vector3
     * @param target: Vector
     */
    public setQuickView(camera: Vector3, target: Vector3) {
        this.mouseControlService.setQuickView(camera, target);
    }

    public resetView() {
        this.cameraService.resetViewOffset();
        this.orbitControlsService.ResetControl();

        if (!this.containerService.imageAligned) {
            this.orbitControlsService.initControls(this.renderService.renderer);
        }
    }


}

// -----------------------------------------------------------------------------------------------------------------------------------------
// -- helper services for structuring data access ------------------------------------------------------------------------------------------
// -----------------------------------------------------------------------------------------------------------------------------------------


// -- patient -----------------------------------------------------------------------------------------------------------------------------

@Injectable({
    providedIn: 'root'
})
class PatientService {

    constructor(private modelDataService: ModelDataService,
                private containerService: ContainerService,
                private textureSizeService: TextureSizeService,
                private loaderService: LoaderService,
                private distanceMeasureService: DistanceMeasureService,
                private shapeService: ShapeService) {
    }

    public get id(): string {
        return this.modelDataService.patientID;
    }

    public set id(value: string) {
        if (value) {
            this.modelDataService.patientID = value;
        }
    }

    public get information(): Patient {
        return this.containerService.patient;
    }

    // TODO: refactor: allow for model props to be independent of actual values
    public set information(patient: Patient) {
        this.containerService.patient = patient;
        this.model.sex = Sex[patient.gender];
        // this.sexChanged(patient.gender);
        this.containerService.targetInfluences = [0, 0, 0, 0, 0, 0, 0];
        this.setAgeInfluences(this.modelDataService.calculateAge(patient.birthdate));
    }

    /**
     * returns the current model configurations
     */
    public get model() {
        return this.modelDataService.type;
    }

    /**
     * Sets if the value of the model has changed.
     * @param val
     */
    set modelChanged(val: boolean) {
        this.textureSizeService.change = val;
    }

    // /**
    //  * check if model is changed
    //  * @returns boolean
    //  */
    // get typeChanged() {
    //     return this.modelDataService.typeChanged;
    // }

    /**
     * Provides the current model height
     * @returns height: number
     */
    get height(): number {
        return this.modelDataService.height;
    }

    /**
     * sets the patient height; it is used to calculate measurements in real world units
     * @param value
     */
    set height(value: number) {
        this.modelDataService.height = value;
        this.distanceMeasureService.measure();
    }

    /**
     * Provides the current model age
     * @returns age: number
     */
    get age(): number {
        return this.modelDataService.age;
    }

    /**
     * sets the patient age
     * @param age
     */
    set age(age: number) {
        this.modelDataService.age = age;
    }

    /**
     * Provides the current model weight
     * @returns weight: number
     */
    get weight(): number {
        return this.modelDataService.weight;
    }

    /**
     * sets the patient weight
     * @param weight
     */
    set weight(weight: number) {
        this.modelDataService.weight = weight;
        this.textureSizeService._modelWeight = weight;
    }

    /**
     * sets the patient bmi
     * @param bmi
     */
    set bmi(bmi: number) {
        this.changeBMI(bmi, true);
    }

    get convertedBMI(): number {
        if (this.modelDataService.bmi) {
            return ((this.modelDataService.bmi - 10) / 5) * 0.3 - 0.3;
        } else {
            return 0;
        }
    }

    /**
     * returns true if any drawing mode is selected (erase-, texture-, poly-paint)
     */
    // get ControlMode() {
    //     return this.containerService.ControlMode;
    // }


    // /**
    //  * check if sex is selected
    //  * @param sex
    //  */
    // public isCurrentSex(sex: string) {
    //     console.log(Sex[sex].toString())
    //     return this.model.sex === Sex[sex];
    // }
    //
    // /**
    //  * check if shape is selected
    //  * @param shape
    //  */
    // public isCurrentShape(shape: string) {
    //     return this.model.shape === BodyShape[shape];
    // }
    //
    // /**
    //  * check if age is selected
    //  * @param age
    //  */
    // public isCurrentAge(age: string) {
    //     return this.model.ageGroup === AgeGroup[age];
    // }

    public changeBMI(bmi: number, update?: boolean) {
        const value = ((bmi - 10) / 5) * 0.3 - 0.3;
        this.modelDataService.bmi = bmi;
        this.weight = bmi * ((this.height / 100) ^ 2);
        this.setTargetInfluence(value, 6, update);
    }


    // TODO: refactor to use target-names
    /**
     * sets the influence of specific target
     * @param value: influence of target
     * @param index: index of target
     * @param update: whether model position should be updated
     */
    public setTargetInfluence(value: number, index: number, update?: boolean) {
        this.shapeService.setTargetInfluence(value, index, update);
    }

    /**
     * returns influence of a specific target
     * @param index: index of target
     */
    public getTargetInfluence(index: number): number {
        return this.containerService.targetInfluences[index];
    }


    /**
     * Load model with applied targets
     * this means vertices were actually moved
     */
    public loadMorphedModel() {
        this.shapeService.loadMorphedModel();
    }


    /**
     * Sets the different targets of the model to represent the correct age.
     * Target 0 --> female_adult to 14.5 years
     * Target 1 --> 14.5 to 8.75 years
     * Target 2 --> 8.75 to 5 years
     * Target 3 --> 5 to 3.25 years
     * Target 4 --> 3.25 to 1.75 years
     * Target 5 --> 1.75 to 0.5 years
     * Target 6 --> corpulent slider
     * @param value age of the model.
     */
    public setAgeInfluences(value: number, update = false) {
        this.shapeService.setAgeInfluences(value, update);
    }


    /**
     * notification if model has been changed, e.g. sex, age, ...
     */
    public changeModel() {
        const uvs = this.loaderService.removeCurrentModel();
        this.loaderService.loadGltfModelFromUrl(this.modelDataService.modelsPath + this.modelDataService.type.toString() + '.gltf', uvs, 1);
        this.modelDataService.changeModel();
    }

    // shape ---------------------------------------------------------------

    /**
     * Updates model if male / female is selected.
     */
    public updateModel() {
        this.modelChanged = true;
        this.modelDataService.type = this.model;
        this.changeModel();
        this.loaderService.loaded.subscribe((loaded) => {
            if (loaded.valueOf() === 1) {
                this.shapeService.surfaceView.resize(true);
                this.initializeShapeView();
                this.loaderService.loaded.observers.pop();
            }
        });
    }

    /**
     * sets the view to display morphable model
     * @param value: surface view
     */
    set shapeView(value: ViewComponent) {
        this.shapeService.surfaceView = value;
        // this.shapeService.surfaceView.resize();
    }

    /**
     * prepares view to display morphable model
     * loads model for model-view
     * prepares morphable model and displays it
     */
    public initializeShapeView() {
        this.shapeService.initializeShapeView();
    }

}


// -- painting -----------------------------------------------------------------------------------------------------------------------------

@Injectable({
    providedIn: 'root'
})
class PaintingService {

    constructor(private containerService: ContainerService,
                private shapeService: ShapeService,
                private colorCodingService: ColorCodingService,
                private mouseControlService: MouseControlsService,
                private textureSizeService: TextureSizeService,
                private brushService: BrushService,
                private sensorService: SensorService,
                private paintService: PaintService,
                private texturePaintService: TexturePaintService,
                private polyPaintService: PolyPaintService
    ) {}


    /**
     * returns true if any drawing mode is selected (erase-, texture-, poly-paint)
     */
    // get ControlMode() {
    //     return this.containerService.ControlMode;
    // }

    /**
     * Returns all possible annotation colors
     * @returns color {burn2a: Color(r, g, b), burn2b: ...}
     */
    get colors(): Labels {
        return this.colorCodingService.colors;
    }

    /**
     * returns the current selected paint color
     * @returns Color(r,g,b)
     */
    get paintColor(): { name: string, color: Color } {
        return this.paintService.paintColor;
    }


    /**
     * sets the current paint color
     * @param color: Color(r, g, b)
     */
    set paintColor(color: { name: string, color: Color }) {
        this.paintService.paintColor = color;
    }

    /**
     * returns the current brush size for painting
     */
    get brushSize(): number {
        return this.brushService.brushSize;
    }

    /**
     * sets the current brush size for painting
     */
    set brushSize(value: number) {
        this.brushService.brushSize = value;
    }

    /**
     * displays the current brush size on the surface view
     */
    public displayBrushSize(height: number, transparent: boolean = false) {
        this.brushService.displayBrushSize(this.brushSize, height, transparent);
    }

    /**
     * removes the brush size from the surface view
     */
    public removeBrushSize() {
        this.brushService.fixateBrushSize(this.brushSize, 100);
    }

    // filters

    /**
     * returns whether filter painting is active
     */
    get marginalFilter(): boolean {
        return this.brushService.marginalFilter;
    }

    /**
     * activates/deactivates filter painting
     * @param value
     */
    set marginalFilter(value: boolean) {
        this.brushService.marginalFilter = value;
    }

    /**
     * returns whether roentgen painting is active
     */
    get roentgen(): boolean {
        return this.brushService.roentgen;
    }

    /**
     * activates/deactivates roentgen painting
     * @param value
     */
    set roentgen(value: boolean) {
        this.brushService.roentgen = value;
    }

    /**
     * returns whether region painting is active
     */
    get regions(): boolean {
        return this.brushService.regions;
    }

    /**
     * activates/deactivates region painting
     * @param value
     */
    set regions(value: boolean) {
        this.brushService.regions = value;
    }

    /**
     * returns whether region painting is possible yet
     * (== face neighbours have been computed)
     */
    get regionsLoading(): boolean {
        return !(this.containerService.loadingStatus === 1);
    }

    public clear() {
        this.polyPaintService.resetDraw();
        this.texturePaintService.resetTexure();
    }

    /**
     * returns assessment selection info
     */
    get assessmentInfo(): MarkingType {
        return this.texturePaintService.selectionInfo;
    }

    public toggleSensor() {
        this.sensorService.toggleSensor();
    }

    /**
     * sets the current paint color for all 4 sensor fields
     * @param val
     */
    set sensorValues(val: { name: string, color: Color }[]) {
        this.sensorService.sensorValues = val;
    }

    get sensorProperty(): Subject<any> {
        return this.sensorService.selectedSensor;
    }

}


// -- image --------------------------------------------------------------------------------------------------------------------------------

@Injectable({
    providedIn: 'root'
})
class ImageService {

    constructor(private pictureService: PictureService,
                private pictureLandmarkService: PictureLandmarkService) {
    }

    /**
     * sets and displays image as overlay for surface view
     * @param img
     */
    set overlayImage(img) {
        this.pictureService.setImage(img);
    }

    /**
     * returns whether image has been fully drawn
     */
    get drawn(): boolean {
        return this.pictureService.pictureDrawn;
    }

    /**
     * returns the current zoom for the overlay image
     */
    get zoom(): number {
        return this.pictureService.zoom;
    }

    /**
     * zooms the overlay image
     * @param value: zoom-factor
     */
    set zoom(value: number) {
        this.pictureService.changeZoom(value);
    }

    /**
     * returns the current opacity of the overlay image
     */
    get opacity(): number {
        return this.pictureService.opacity;
    }

    /**
     * changes the opacity of the overlay image
     * @param value
     */
    set opacity(value: number) {
        this.pictureService.changeOpacity(value);
    }

    /**
     * rotates overlay image in a given direction and with a given angle (rad)
     * @param direction
     * @param angle
     */
    public rotate(direction: string, angle: number) {
        this.pictureService.rotateOverlay(direction, angle);
    }

    // TODO: change back camera up
    public reset() {
        this.pictureService.resetImg();
        this.clearLandmarks();
        this.pictureLandmarkService.resetTransformations();
    }

    public clear() {
        this.pictureService.clear();
    }


    /**
     * returns whether dragging of image is active
     */
    get dragEnabled(): boolean {
        return this.pictureService.getMoveStatus();
    }

    // landmarks ----------------------------------------------------------

    /**
     * returns whether image or model landmarks are currently being placed
     */
    get landmarkMode(): string {
        return this.pictureLandmarkService.imageActive ? 'image' : 'model';
    }

    /**
     * switches between placement of image and model landmarks
     */
    public switchLandmarkMode() {
        this.pictureLandmarkService.switchMode();
    }

    /**
     * returns count of currently placed image landmarks
     */
    get imageLandmarkCount(): number {
        return this.pictureLandmarkService.numImageMarkers;
    }

    /**
     * returns count of currently placed model landmarks
     */
    get modelLandmarkCount(): number {
        return this.pictureLandmarkService.numModelMarkers;
    }

    /**
     * return whether landmarks are currently visible
     */
    get landmarksVisible(): boolean {
        return this.pictureLandmarkService.markersVisible;
    }

    /**
     * sets visibility of landmarks
     */
    set landmarksVisible(value: boolean) {
        this.pictureLandmarkService.markersVisible = value;
    }

    /**
     * realigns landmarks after transformations have been applied
     */
    public realignLandmarks() {
        this.pictureLandmarkService.realignLandmarks();
    }

    /**
     * removes all landmarks
     */
    public clearLandmarks() {
        this.pictureLandmarkService.clearLandmarks();
    }

    public resetTransformations() {
        this.pictureLandmarkService.resetTransformations();
    }
}

